import React, { useState } from 'react'
import classNames from 'classnames'
import { ABSpinner, Button } from '@abroad/components'
import TimeSelection from '../form/TimeSelection'
import Toggle from '../common/Toggle'
import { PlusIcon, DuplicateIcon, DeleteIcon } from '../assetsComponents/common'

const WEEK_DAYS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
]

const WeeklyAvailabilitySchedule = ({
  availability = [],
  onToggleDay,
  onTimeSelectionUpdate,
  onAddTimeRange,
  onRemoveTimeRange,
  onAvailabilityChange,
  isLoading = false,
  onCancel,
  onSave,
  isSaving = false,
  hasAnyTimeRange = false,
}) => {
  // Simplified copy state - single object instead of multiple states
  const [copyState, setCopyState] = useState({
    sourceDay: null,
    targetDays: [],
  })

  const getMinFromTime = (dayData, rangeIndex) => {
    if (rangeIndex === 0) return null
    return dayData.timeRanges[rangeIndex - 1]?.end || null
  }

  const handleOpenCopy = (dayName) => {
    setCopyState((prev) => ({
      sourceDay: prev.sourceDay === dayName ? null : dayName,
      targetDays: [],
    }))
  }

  const handleToggleCopyTarget = (targetDay) => {
    setCopyState((prev) => ({
      ...prev,
      targetDays: prev.targetDays.includes(targetDay)
        ? prev.targetDays.filter((day) => day !== targetDay)
        : [...prev.targetDays, targetDay],
    }))
  }

  const handleApplyCopy = () => {
    const { sourceDay, targetDays } = copyState
    if (!sourceDay || targetDays.length === 0) {
      setCopyState({ sourceDay: null, targetDays: [] })
      return
    }

    const source = availability.find((day) => day.day === sourceDay)
    if (!source) {
      setCopyState({ sourceDay: null, targetDays: [] })
      return
    }

    // Notify parent component about the copy operation
    if (onAvailabilityChange) {
      const updatedAvailability = availability.map((day) =>
        targetDays.includes(day.day)
          ? {
              ...day,
              isActive: source.isActive,
              timeRanges: source.timeRanges.map((range) => ({ ...range })),
            }
          : day,
      )
      onAvailabilityChange(updatedAvailability)
    }

    setCopyState({ sourceDay: null, targetDays: [] })
  }

  const renderCopyPopover = (sourceDay) => (
    <div className='availability-copy-popover'>
      <p className='availability-copy-popover__title'>Copy times to...</p>
      <div className='availability-copy-popover__list'>
        {WEEK_DAYS.filter((weekday) => weekday !== sourceDay).map((weekday) => (
          <label
            key={`${sourceDay}-${weekday}`}
            className='availability-copy-popover__checkbox'>
            <input
              type='checkbox'
              checked={copyState.targetDays.includes(weekday)}
              onChange={() => handleToggleCopyTarget(weekday)}
            />
            <span>{weekday}</span>
          </label>
        ))}
      </div>
      <button
        type='button'
        className='btn btn-saffron w-100 text-uppercase s8'
        onClick={handleApplyCopy}
        disabled={copyState.targetDays.length === 0}>
        Apply
      </button>
    </div>
  )

  return isLoading ? (
    <div className='d-flex justify-content-center py-5'>
      <ABSpinner />
    </div>
  ) : (
    <div className='availability-weekly-wrapper'>
      <section className='availability-weekly mt-3'>
        <div className='d-flex justify-content-between align-items-center my-2'>
          <div>
            <h6 className='question-title'>Available time</h6>
          </div>
        </div>
        <div className='availability-weekly__list'>
          {availability.map((day) => (
            <div
              className={classNames('availability-day-row', {
                'availability-day-row--inactive': !day.isActive,
              })}
              key={day.day}>
              <div className='availability-day-row__left pt-2'>
                <Toggle
                  id={`toggle-${day.day}`}
                  checked={day.isActive}
                  onChange={() => onToggleDay(day.day)}
                  size='medium'
                />
                <p className='availability-day-row__label mb-0'>{day.day}</p>
              </div>

              <div className='availability-day-row__right'>
                <div
                  className='availability-time-ranges'
                  style={{
                    visibility: day.isActive ? 'visible' : 'hidden',
                    opacity: day.isActive ? 1 : 0,
                    pointerEvents: day.isActive ? 'auto' : 'none',
                  }}>
                  {day.timeRanges.map((range, index) => (
                    <div
                      className='availability-time-row'
                      key={`${day.day}-range-${index}`}>
                      <TimeSelection
                        fromTime={range.start || ''}
                        toTime={range.end || ''}
                        onTimeChange={(from, to) =>
                          onTimeSelectionUpdate(day.day, index, from, to)
                        }
                        usdedInScheduling
                        handleBlur={() => {}}
                        minFromTime={getMinFromTime(day, index)}
                        hideDisabledOptions={true}
                      />
                      <button
                        type='button'
                        className='availability-icon-btn'
                        onClick={() => onRemoveTimeRange(day.day, index)}
                        style={{
                          visibility:
                            day.isActive && day.timeRanges.length > 1
                              ? 'visible'
                              : 'hidden',
                        }}>
                        <DeleteIcon />
                      </button>
                    </div>
                  ))}
                </div>
                <div
                  className='availability-row-actions pt-2'
                  style={{
                    visibility: day.isActive ? 'visible' : 'hidden',
                    opacity: day.isActive ? 1 : 0,
                    pointerEvents: day.isActive ? 'auto' : 'none',
                  }}>
                  <button
                    type='button'
                    className='availability-icon-btn'
                    onClick={() => onAddTimeRange(day.day)}
                    aria-label={`Add time range for ${day.day}`}>
                    <PlusIcon />
                  </button>
                  <div className='availability-copy-wrapper'>
                    <button
                      type='button'
                      className='availability-icon-btn'
                      onClick={() => handleOpenCopy(day.day)}
                      aria-label={`Copy ${day.day} schedule`}>
                      <DuplicateIcon />
                    </button>
                    {copyState.sourceDay === day.day &&
                      renderCopyPopover(day.day)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>
      {onCancel && onSave && (
        <div className='mt-3 d-flex justify-content-end'>
          <Button
            variant='outline-dark'
            className='mr-3 text-uppercase s8'
            onClick={onCancel}
            disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant='saffron'
            className='save-btn-fixed-width'
            onClick={onSave}
            isLoading={isSaving}
            disabled={isLoading}>
            Save
          </Button>
        </div>
      )}
    </div>
  )
}

export default WeeklyAvailabilitySchedule
